/**
 * Represents a product with its handle and a specific variant ID.
 * @typedef {object} Product
 * @property {string} [handle] - The unique identifier or handle for the product.
 * @property {string} [defaultHandle] - The default unique identifier or handle for the product.
 * @property {number} [variantId] - The product variant id.
 * @property {number} [sellingPlanId] - The product selling plan id.
 *
 * Configuration object for the subscription checkout process.
 * @typedef {object} SignupConfig
 * @property {Product} mainProduct - The subscription signup product.
 * @property {Product} [addonProduct] - An optional add-on product.
 * @property {Product} [nextProduct] - An optional subscription continuity product.
 * @property {string} [email] - An optional email to pre-fill the checkout form.
 * @property {string} [discountCode] - An optional discount code to apply at checkout.
 * @property {string} [addonDiscountCode] - An optional additional discount code to apply at checkout.
 * @property {object} [customProperties] - An custom line item properties.
 *
 * @typedef {object} CartItem Represents an item to be added to the cart.
 * @property {number} id - The unique identifier for the product variant.
 * @property {number} selling_plan - The ID of the selling plan (e.g., for subscriptions).
 * @property {number} quantity - The quantity of the item to be purchased.
 * @property {object | null} properties - Additional properties for the item.
 *
 */

class SubscriptionCart {
  /** @type {Product | null} */
  #signupProduct = null;
  /** @type {Product | null} */
  #addonProduct = null;
  /** @type {Product | null} */
  #nextProduct = null;
  /** @type {Object | null} */
  #customProperties = null;
  /** @type {string | null} */
  #discountCode = null;
  /** @type {string | null} */
  #addonDiscountCode = null;
  /** @type {string | null} */
  #email = null;

  constructor() {}

  /**
   * Initiates the checkout process with a given configuration.
   *
   * @param {SignupConfig} signupConfig - The configuration object detailing the products and options for the checkout.
   * @param {boolean} shouldClearCart - If true, the existing cart will be cleared before adding the new products.
   */
  async checkout(signupConfig, shouldClearCart = false) {
    if (!this.#isSignupProductValid(signupConfig.mainProduct, signupConfig.discountCode)) {
      throw new Error('No signup item was specified.');
    }

    this.#signupProduct = signupConfig.mainProduct;
    this.#addonProduct = signupConfig.addonProduct;
    this.#nextProduct = signupConfig.nextProduct;
    this.#customProperties = signupConfig.customProperties;
    this.#discountCode = signupConfig.discountCode;
    this.#addonDiscountCode = signupConfig.addonDiscountCode;
    this.#email = signupConfig.email;

    console.time('TIMER add to cart');

    const signupItems = this.#getSignupItems();

    if (shouldClearCart) await this.#clearCart();

    await this.#addToCart(signupItems);

    console.timeEnd('TIMER add to cart');

    await this.#attachAnalyticsData();

    this.#navigateToCheckout();
  }

  #isSignupProductValid(signupProduct, discountCode) {
    const isDirectProduct = signupProduct.variantId && signupProduct.sellingPlanId;
    return signupProduct.handle || isDirectProduct || discountCode || signupProduct.defaultHandle;
  }

  #getSignupItems() {
    const items = [];

    const mainSignupItem = this.#getMainSignupItem();

    items.push(mainSignupItem);

    if (this.#addonProduct?.variantId) {
      items.push({
        id: this.#addonProduct.variantId,
        selling_plan: null,
        quantity: 1,
      });
    }

    return items;
  }

  #getMainSignupItem() {
    const { variantId, sellingPlanId } = this.#getVariantAndSellingPlanIds();

    const hasProperties = Boolean(this.#nextProduct?.variantId || this.#customProperties);

    let properties = {};

    if (this.#nextProduct?.variantId) {
      properties['_next_product_variant_id'] = this.#nextProduct.variantId;
    }

    if (this.#customProperties) {
      properties = { ...properties, ...this.#customProperties };
    }

    const subscriptionItem = {
      id: variantId,
      selling_plan: sellingPlanId,
      quantity: 1,
      ...(hasProperties ? { properties } : {}),
    };

    return subscriptionItem;
  }

  #getVariantAndSellingPlanIds() {
    if (this.#signupProduct.variantId && this.#signupProduct.sellingPlanId) {
      return {
        variantId: this.#signupProduct.variantId,
        sellingPlanId: this.#signupProduct.sellingPlanId,
      };
    }

    if (this.#signupProduct.handle === 'eu_gotm_g_and_t') {
      return {
        variantId: 43601700257972,
        sellingPlanId: 1518370996,
      };
    }

    if (this.#signupProduct.handle || this.#discountCode || this.#signupProduct.defaultHandle) {
      // Get from Metaobject using product handle (uses discount code to get handle if not already set)
      const product = this.#getFromAcquisitionMetaObject();

      return {
        variantId: product.variantId,
        sellingPlanId: product.sellingPlanId,
      };
    }

    throw new Error("Couldn't find variant id and selling plan id of the signup product.");
  }

  #getFromAcquisitionMetaObject() {
    const onboardingProducts = window.__CGC.onboardingProducts;

    if (!onboardingProducts) {
      throw new Error('No onboardingProducts is set');
    }

    const productHandle =
      this.#signupProduct.handle || this.#getProductHandleByDiscountCode() || this.#signupProduct.defaultHandle;

    const product = onboardingProducts[productHandle];

    if (!product) {
      throw new Error("Couldn't find product from Aquasition: Welcome products ");
    }

    if (!this.#discountCode) {
      this.#discountCode = product.defaultDiscountCode;
    }

    return product;
  }

  #getProductHandleByDiscountCode() {
    const isTasterBoxContinuity = this.#discountCode.startsWith('OFF-TASTER');
    const isTasterBox4 = this.#discountCode.startsWith('SIP-3-');
    const isTasterBox3 = this.#discountCode.startsWith('GIN-TASTER-3-');
    const isTasterBox2 =
      this.#discountCode.startsWith('SIP-2-') || ['SIP-TASTER-FB2', 'SIP-TASTER-SOV2'].includes(this.#discountCode);
    const isTasterBox =
      !isTasterBox2 && (this.#discountCode.startsWith('SIP-') || this.#discountCode.startsWith('MM-SIP-'));

    const isRumTasterBox2 = this.#discountCode.startsWith('RUM-TASTER-2-');
    const isRumTasterBox = !isRumTasterBox2 && this.#discountCode.startsWith('RUM-TASTER-');

    const isGotmDiscount = this.#discountCode === '30OFF-HP';

    let pickedProductHandle = this.#signupProduct.handle || this.#signupProduct.defaultHandle;

    if (isTasterBoxContinuity) {
      pickedProductHandle = 'gotm_taster_continuity';
    } else if (isRumTasterBox2) {
      pickedProductHandle = 'rotm_taster_2';
    } else if (isRumTasterBox) {
      pickedProductHandle = 'rotm_taster';
    } else if (isTasterBox4) {
      pickedProductHandle = 'gotm_taster_4';
    } else if (isTasterBox3) {
      pickedProductHandle = 'gotm_taster_3';
    } else if (isTasterBox2) {
      pickedProductHandle = 'gotm_taster_2';
    } else if (isTasterBox) {
      pickedProductHandle = 'gotm_taster';
    } else if (!(isTasterBox || isTasterBox2 || isTasterBox3) && pickedProductHandle.startsWith('gotm_taster')) {
      pickedProductHandle = 'gotm';
    } else if (isGotmDiscount) {
      pickedProductHandle = 'gotm';
    }

    return pickedProductHandle;
  }

  #navigateToCheckout() {
    const checkoutUrl = this.#getCheckoutUrl();

    window.location.assign(checkoutUrl);
  }

  #getCheckoutUrl() {
    if (this.#email && !this.#discountCode) {
      return `/checkout?checkout[email]=${encodeURIComponent(this.#email)}`;
    }

    const discountString = this.#addonDiscountCode
      ? `${this.#discountCode},${this.#addonDiscountCode}`
      : this.#discountCode;

    if (!this.#email && discountString) {
      return `/checkout?discount=${discountString}`;
    }

    return `/checkout?checkout[email]=${encodeURIComponent(this.#email)}&discount=${discountString}`;
  }

  async #addToCart(cartItems) {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: cartItems,
      }),
    });

    return response;
  }

  async #clearCart() {
    console.time('TIMER clear cart');
    await fetch(`${window.Shopify.routes.root}cart/clear.js`);
    console.timeEnd('TIMER clear cart');
  }

  async #attachAnalyticsData() {
    try {
      console.time('TIMER attach analytics');
      const { gaClientId, gaSessionId, fbp, fbc } = await getAnalyticsData();

      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            ga_client_id: gaClientId,
            ga_session_id: gaSessionId,
            fbp: fbp,
            fbc: fbc,
          },
        }),
      });
      console.timeEnd('TIMER attach analytics');
    } catch (error) {
      console.error('Error attaching analytics data:', error);
    }
  }
}
